import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  addTagSvg,
  checkSvg,
  deleteTagSvg,
  downloadSvg,
  editSvg,
  trashSvg,
  unverifiedSvg,
} from "../assets/icons";
import { showConfirmToast } from "../components/ConfirmToast";
import { TournamentScrollContext } from "../container/TournamentContainer";
import {
  deleteSponsor,
  deleteUser,
  getAllSponsors,
  getAllUsers,
  verifyLogo,
} from "../data/httpClient";
import { Sponsor, User } from "../types";
import { notify } from "../utils/notify";

export const UsersOrSponsorsPage = () => {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const scrollContext = useContext(TournamentScrollContext);

  const [tabSelected, setTabSelected] = useState(() => {
    const tab = searchParams.get("tab");
    return tab === "user" ? "user" : "sponsor";
  });

  const [missingLogoSelected, setMissingLogoSelected] = useState(false);
  const [unapprovedLogoSelected, setUnapprovedLogoSelected] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);

  const handleNavigation = (tab: string) => {
    setTabSelected(tab);
    setSearchParams({ tab }, { replace: true });
  };

  const getData = async () => {
    if (tabSelected === "user") {
      const result = await getAllUsers();
      setUsers(result);
    } else {
      let result = await getAllSponsors();
      if (missingLogoSelected) {
        result = result.filter((sponsor: Sponsor) => {
          return !sponsor.logos.length;
        });
      }
      if (unapprovedLogoSelected) {
        result = result.filter((sponsor: Sponsor) => {
          // Check if the logos array exists and has at least one item
          // at this moment in time logos are an array. We will probably be changing that to a single item.
          if (sponsor.logos && sponsor.logos.length > 0) {
            // Return true or false based on the verified property of the logo
            return !sponsor.logos[0].verified;
          }
          // If there's no logos array or it's empty, treat it as not meeting the condition
          return false;
        });
      }
      setSponsors(result);
    }
  };

  // ensure scroll is at the top of the page (needed when the user navigates back)
  useEffect(() => {
    if (scrollContext) {
      scrollContext.scrollToTop();
    }
  }, []);

  useEffect(() => {
    getData();
  }, [tabSelected, missingLogoSelected, unapprovedLogoSelected]);

  // Keep state in sync if the query param changes
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && tab !== tabSelected) {
      setTabSelected(tab === "user" ? "user" : "sponsor");
    }
  }, [searchParams]);

  return (
    <div className="usersPageWrapper adminUsersPage">
      <section className="tabWrapper">
        <button
          className={`userTabBtn ${tabSelected === "sponsor" && "selected"}`}
          onClick={() => handleNavigation("sponsor")}
        >
          Sponsors
        </button>
        <button
          className={`userTabBtn ${tabSelected === "user" && "selected"}`}
          onClick={() => handleNavigation("user")}
        >
          Users
        </button>
      </section>

      <section className="userContentWrapper">
        <div className="topWrapper">
          <button
            className="addNewUserBtn"
            style={{ marginRight: "1em" }}
            onClick={() => {
              navigate(`/tournaments/${tournamentId}/admin/user-sponsor/new`, {
                state: { type: tabSelected },
              });
            }}
          >
            Add new {tabSelected}
          </button>

          <button
            className="addNewUserBtn"
            onClick={() => {
              navigate(`/tournaments/${tournamentId}/admin/userSponsor/new`, {
                state: { type: tabSelected },
              });
            }}
          >
            Link User to Sponsor
          </button>

          {tabSelected === "sponsor" && (
            <article className="filterWrapper">
              <button
                className={`filterTag ${missingLogoSelected && "tagSelected"}`}
                onClick={() => {
                  setMissingLogoSelected(!missingLogoSelected);
                }}
              >
                <span>Missing logos</span>
                {missingLogoSelected ? (
                  <img src={deleteTagSvg} alt="unselected filter tag" />
                ) : (
                  <img src={addTagSvg} alt="selected filter tag" />
                )}
              </button>
              <button
                className={`filterTag ${
                  unapprovedLogoSelected && "tagSelected"
                }`}
                onClick={() => {
                  setUnapprovedLogoSelected(!unapprovedLogoSelected);
                }}
              >
                <span>Unapproved logos</span>
                {unapprovedLogoSelected ? (
                  <img src={deleteTagSvg} alt="unselected filter tag" />
                ) : (
                  <img src={addTagSvg} alt="selected filter tag" />
                )}
              </button>
            </article>
          )}
        </div>
        <div>
          {" "}
          {/* Div that wraps both forms */}
          {tabSelected === "sponsor" ? (
            // sponsors table
            <table className="usersTable">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Logo</th>
                  <th>Download Logo</th>
                  <th>Logo Verified</th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sponsors.map((u: Sponsor, i: number) => {
                  return (
                    <tr key={i}>
                      <td className="nameColumn">{u.name}</td>
                      <td className="descriptionTableColumn">
                        {u.description.length > 140
                          ? `${u.description.slice(0, 137)}...`
                          : u.description}
                      </td>
                      <td>
                        {u.logos.length > 0 ? (
                          <>
                            {/* <p>{u.logos.length}</p>  */}
                            <img
                              src={u.logos[0]?.imageUrl}
                              style={{ maxWidth: "100px" }}
                              alt="sponsor logo"
                            />
                          </>
                        ) : (
                          <p>No logo present</p>
                        )}
                      </td>
                      <td>
                        <a
                          href={u.logos[0]?.imageUrl}
                          download
                          target="_blank"
                          style={{ cursor: "pointer", display: "inline-block" }} // Ensures the area is clickable and changes the cursor to indicate this
                          rel="noopener noreferrer"
                        >
                          <img
                            src={downloadSvg}
                            className="iconAction"
                            alt="download logo"
                          />
                        </a>
                      </td>
                      <td>
                        {u.logos &&
                        u.logos.length > 0 &&
                        u.logos[0]?.verified ? (
                          <button
                            className="verifyBtn"
                            onClick={async () => {
                              showConfirmToast({
                                title: "Unverify Logo",
                                message:
                                  "Are you sure you want to unverify this logo?",
                                onConfirm: async () => {
                                  u.logos[0].verified = false;
                                  await verifyLogo(u.logos[0].id, false);
                                  notify.success(
                                    "Logo unverified successfully."
                                  );
                                  const result = await getAllSponsors();
                                  setSponsors(result);
                                },
                              });
                            }}
                          >
                            <img
                              src={checkSvg}
                              className="iconAction"
                              alt="logo is verified"
                            />
                          </button>
                        ) : u.logos && u.logos.length > 0 ? (
                          <button
                            className="verifyBtn"
                            onClick={async () => {
                              showConfirmToast({
                                title: "Verify Logo",
                                message:
                                  "Are you sure you want to verify this logo?",
                                onConfirm: async () => {
                                  u.logos[0].verified = true;
                                  await verifyLogo(u.logos[0].id, true);
                                  notify.success("Logo verified successfully.");
                                  const result = await getAllSponsors();
                                  setSponsors(result);
                                },
                              });
                            }}
                          >
                            <img
                              src={unverifiedSvg}
                              className="iconAction"
                              alt="logo is unverified"
                            />
                          </button>
                        ) : (
                          <button
                            className="verifyBtn"
                            onClick={() => {
                              notify.info(
                                "Please upload a logo before verifying."
                              );
                            }}
                          >
                            <img
                              src={unverifiedSvg}
                              className="iconAction"
                              alt="logo is unverified"
                            />
                          </button>
                        )}
                      </td>
                      <td>
                        <button
                          className="iconBtn"
                          onClick={() => {
                            navigate(
                              `/tournaments/${tournamentId}/admin/user-sponsor/${u.id}/edit`,
                              {
                                state: { type: tabSelected },
                              }
                            );
                          }}
                        >
                          <img
                            src={editSvg}
                            className="iconAction"
                            alt="edit sponsor"
                          />
                        </button>
                      </td>
                      <td>
                        <button
                          className="iconBtn"
                          onClick={async () => {
                            showConfirmToast({
                              title: "Delete Sponsor",
                              message:
                                "Are you sure you want to delete this sponsor?",
                              btnText: "Delete",
                              onConfirm: async () => {
                                await deleteSponsor(u.id);
                                notify.success("Sponsor deleted successfully.");
                                const result = await getAllSponsors();
                                setSponsors(result);
                              },
                            });
                          }}
                        >
                          <img
                            src={trashSvg}
                            className="iconAction"
                            alt="delete sponsor"
                          />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            // Users table

            <table className="usersTable">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Roles</th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u: User, i: number) => {
                  return (
                    <tr key={i}>
                      <td className="nameColumn">{u.email}</td>
                      <td>{u.nameFirst}</td>
                      <td>{u.nameLast}</td>
                      <td>
                        {u.roleArray && u.roleArray.length > 0
                          ? u.roleArray.join(", ")
                          : "-"}
                      </td>
                      <td>
                        <button
                          className="iconBtn"
                          onClick={() => {
                            navigate(
                              `/tournaments/${tournamentId}/admin/user-sponsor/${u.id}/edit`,
                              {
                                state: { type: tabSelected },
                              }
                            );
                          }}
                        >
                          <img
                            src={editSvg}
                            className="iconAction"
                            alt="edit user"
                          />
                        </button>
                      </td>
                      <td>
                        <button
                          className="iconBtn"
                          onClick={async () => {
                            showConfirmToast({
                              title: "Delete User",
                              message:
                                "Are you sure you want to delete this user?",
                              btnText: "Delete",
                              onConfirm: async () => {
                                await deleteUser(u.id);
                                notify.success("User deleted successfully.");
                                const result = await getAllUsers();
                                setUsers(result.reverse());
                              },
                            });
                          }}
                        >
                          <img
                            src={trashSvg}
                            className="iconAction"
                            alt="delete user"
                          />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>{" "}
        {/* END Div that wraps both forms */}
      </section>
    </div>
  );
};
